/**
 * SlideNavController
 * .slide の横スクロールを .slide-nav の矢印 / セグメント状インジケータで操作する
 *
 * HTML 構造:
 * <section>
 *   <div class="slide">
 *     <div class="slide__horizontal">...</div>
 *   </div>
 *   <div class="slide-nav">
 *     <button class="slide-nav__arrow slide-nav__arrow--prev">...</button>
 *     <div class="slide-nav__scrollbar">
 *       <!-- .slide-nav__thumb はスライド数分 JS で動的生成 -->
 *     </div>
 *     <button class="slide-nav__arrow slide-nav__arrow--next">...</button>
 *   </div>
 * </section>
 */
export const SlideNavController = {
  init() {
    document.querySelectorAll( '.slide-nav' ).forEach( ( nav ) => {
      const section = nav.closest( 'section' );
      // ★ .slide は「.slide-nav の直接の親コンテナ内」で探す
      //   例) <div class="history-pc"><div class="slide">...</div><div class="slide-nav">...</div></div>
      //   section 直下から探すと、PC/SP で複数 .slide が並ぶ場合に
      //   常に最初の .slide (=PC) を掴んでしまい SP nav が機能しなくなる
      const slide = nav.parentElement?.querySelector( '.slide' )
                 ?? section?.querySelector( '.slide' );
      if ( !slide ) return;

      const prev      = nav.querySelector( '.slide-nav__arrow--prev' );
      const next      = nav.querySelector( '.slide-nav__arrow--next' );
      const scrollbar = nav.querySelector( '.slide-nav__scrollbar' );
      const panels    = slide.querySelectorAll( '.slide__horizontal' );
      if ( !scrollbar || !panels.length ) return;

      // 既存の thumb 要素をクリアし、パネル数だけ生成する
      scrollbar.innerHTML = '';
      const thumbs = [];
      panels.forEach( ( _, i ) => {
        const t = document.createElement( 'div' );
        t.className = 'slide-nav__thumb';
        if ( i === 0 ) t.classList.add( 'is-active' );
        t.addEventListener( 'click', () => {
          slide.scrollTo( { left: i * slide.clientWidth, behavior: 'smooth' } );
        } );
        scrollbar.appendChild( t );
        thumbs.push( t );
      } );

      // ★ SP 用: セグメント thumbs を隠して連続 progress バーで現在位置を可視化する
      //   PC は CSS 側で display:none にしているため付与コストは無い
      const progress = document.createElement( 'div' );
      progress.className = 'slide-nav__progress';
      scrollbar.appendChild( progress );

      // ★ scrollbar (track) を直接クリック → その位置へジャンプ
      //   PC の thumb セグメントには thumb 自身が click handler を持つため、
      //   その伝播はスキップ (e.target が thumb の場合は何もしない)。
      //   これで矢印非表示の SP でも、track 左端クリックで先頭に戻せる。
      scrollbar.addEventListener( 'click', ( e ) => {
        if ( e.target.classList.contains( 'slide-nav__thumb' ) ) return;
        const rect  = scrollbar.getBoundingClientRect();
        const ratio = rect.width > 0
          ? Math.max( 0, Math.min( 1, ( e.clientX - rect.left ) / rect.width ) )
          : 0;
        const max = slide.scrollWidth - slide.clientWidth;
        slide.scrollTo( { left: ratio * max, behavior: 'smooth' } );
      } );

      const update = () => {
        const max = slide.scrollWidth - slide.clientWidth;
        // 現在の中央にあるパネルを active とみなす (0..panels.length-1)
        const index = slide.clientWidth > 0
          ? Math.round( slide.scrollLeft / slide.clientWidth )
          : 0;
        const active = Math.min( panels.length - 1, Math.max( 0, index ) );
        thumbs.forEach( ( t, i ) => t.classList.toggle( 'is-active', i === active ) );
        if ( prev ) prev.disabled = slide.scrollLeft <= 1;
        if ( next ) next.disabled = slide.scrollLeft >= max - 1;

        // SP progress bar: 可視領域比 (width) と オーバーフロー内での位置 (left) を更新
        if ( slide.scrollWidth > 0 && slide.clientWidth > 0 ) {
          const visibleRatio = Math.min( 1, slide.clientWidth / slide.scrollWidth );
          const scrollRatio  = max > 0 ? slide.scrollLeft / max : 0;
          progress.style.width = `${ visibleRatio * 100 }%`;
          progress.style.left  = `${ scrollRatio * ( 1 - visibleRatio ) * 100 }%`;
        }

        // ★ #history 専用: --1/--2 (index 0/1) は wine、--3 以降は green に切替
        //   + 1スライド目 (index 0) では .section-title を非表示 → 2枚目以降でフェードイン
        //   ※ SP の .history-sp は年代数がPCと異なるため、PC 側 (.history-pc) の
        //     .slide-nav でのみトリガーする
        if ( section?.id === 'history' && nav.closest( '.history-pc' ) ) {
          section.classList.toggle( 'is-green', active >= 2 );
          section.classList.toggle( 'is-scrolled', active >= 1 );
        }

        // ★ #history SP: chapter 3 の入り具合で wine → green を scrub
        //   .history-sp__chapter--3 が viewport 右端に触れ始めた瞬間から
        //   viewport 左端に到達するまでの区間で --history-scrub を 0→1 に補間
        if ( section?.id === 'history' && nav.closest( '.history-sp' ) ) {
          const ch3 = slide.querySelector( '.history-sp__chapter--3' );
          if ( ch3 && slide.clientWidth > 0 ) {
            const start = ch3.offsetLeft - slide.clientWidth;
            const end   = ch3.offsetLeft;
            const range = Math.max( 1, end - start );
            const t = Math.max( 0, Math.min( 1, ( slide.scrollLeft - start ) / range ) );
            section.style.setProperty( '--history-scrub', t );
          }
        }
      };

      // 矢印クリックは「次/前の panel 境界」にスナップさせる。
      //   通常スクロールでは snap を効かせていないため、任意の中間位置に
      //   停まっている可能性がある。scrollBy(clientWidth) だと現在位置 + 幅
      //   になり panel 境界からズレるので、現在位置から見て次/前の panel の
      //   offsetLeft を計算して scrollTo する。
      //   ※ Windows は scrollTo({ left: N*w }) 後の scrollLeft が subpixel 単位で
      //     ズレる (例: N*w - 0.5) ため、floor/ceil ベースだと現在 index と同じ値が返り
      //     矢印が動かなくなる (最後の1枚 <-> 手前で発生しやすい)。
      //     現在 index は Math.round で確定してから ±1 する。
      prev?.addEventListener( 'click', () => {
        const w = slide.clientWidth;
        if ( w <= 0 ) return;
        const current = Math.round( slide.scrollLeft / w );
        const target  = Math.max( 0, current - 1 );
        slide.scrollTo( { left: target * w, behavior: 'smooth' } );
      } );
      next?.addEventListener( 'click', () => {
        const w = slide.clientWidth;
        if ( w <= 0 ) return;
        const current = Math.round( slide.scrollLeft / w );
        const target  = Math.min( panels.length - 1, current + 1 );
        slide.scrollTo( { left: target * w, behavior: 'smooth' } );
      } );

      // ★ Windows マウスホイール対応
      //   Mac トラックパッドは deltaX が出るのでネイティブに横スクロールできるが、
      //   Windows のマウスホイールは deltaY のみ。加えてページ側の Lenis が
      //   window の wheel を乗っ取るため、そのままだと縦回転が縦スクロールとして
      //   ページを流してしまい、.slide の横スクロールがまったく効かなくなる。
      //   → deltaY を scrollLeft に振り替え、余白があるうちは consume、
      //     端に達したら stopPropagation せずに Lenis に返してページ縦スクロールへ。
      //   ※ Mac ではトラックパッドの縦フリックまで横に流れてしまうため、
      //     非 Mac 環境 (Windows/Linux) 限定で有効化する。
      //   ※ セクションが viewport を完全に覆っている時だけ横変換する。
      //     そうしないと、history に差し掛かった瞬間や通り過ぎる途中で縦スクロールが
      //     横に吸われて、ページを縦に流したいだけのユーザーが history に閉じ込められる。
      const isMac = /Mac|iPhone|iPad|iPod/.test( navigator.platform );
      if ( !isMac ) {
        slide.addEventListener( 'wheel', ( e ) => {
          // トラックパッドで横方向に払っている場合はネイティブ挙動に任せる
          if ( Math.abs( e.deltaX ) > Math.abs( e.deltaY ) ) return;
          const max = slide.scrollWidth - slide.clientWidth;
          if ( max <= 0 ) return; // 横方向にオーバーフローなし (SP 等)
          // セクションが viewport 全体を覆っている時のみ横変換
          const rect = ( section ?? slide ).getBoundingClientRect();
          const vh   = window.innerHeight;
          if ( rect.top > 0 || rect.bottom < vh ) return;
          const goingRight = e.deltaY > 0;
          const atEnd   = slide.scrollLeft >= max - 1;
          const atStart = slide.scrollLeft <= 0;
          if ( ( goingRight && atEnd ) || ( !goingRight && atStart ) ) return;
          e.preventDefault();
          e.stopPropagation();
          slide.scrollLeft += e.deltaY;
        }, { passive: false } );
      }

      slide.addEventListener( 'scroll', update, { passive: true } );
      window.addEventListener( 'resize', update );
      update();
    } );
  },
};
